import { createLogger } from '@svs/nova-core/log';

import { type CouponId, type CouponIndex, getCouponIndices } from '../../../../../schema/coupon/entry.js';
import { localStorageSchema } from '../../../../../schema/input/entry.js';
import { entriesFromObject } from '../../../../../schema/utils/entry.js';
import type { CouponState } from '../../../../domain/entry.js';

const logger = createLogger('poolbetting.parse-coupons');

/**
 * Parses unknown data payload to correct TypeScript type.
 * This is used as a reader function for the the local storage middleware in Zustand Coupon store.
 *
 * @param data Data to parse to a CouponState
 * @returns Parsed CouponState
 */
export function parseCoupons(data: unknown): CouponState | undefined {
  try {
    const { Coupons = {}, Wallets = {} } = localStorageSchema.parse(data); // TODO: Check this if error should be handled in schema to prevent all individual cases to handle error. safeParse??

    // Ensure Coupons is a full Record with no undefined values
    const normalizedCoupons: CouponState['Coupons'] = Object.fromEntries(
      Object.entries(Coupons).map(([couponId, coupon]) => [couponId, coupon ?? []])
    );

    const ModifiedCoupons: CouponState['ModifiedCoupons'] = {};
    for (const [couponId, coupon] of entriesFromObject(normalizedCoupons)) {
      const couponIndexes = getCouponIndices(coupon);
      ModifiedCoupons[couponId] = couponIndexes;
    }

    // Ensure Wallets is a full Record with no undefined values
    // And ensure that coupon exists for each wallets entry
    const normalizedWallets: CouponState['Wallets'] = Object.fromEntries(
      Object.entries(Wallets).map(([couponId, wallet]) => {
        const couponIndices: CouponIndex[] = ModifiedCoupons[couponId as CouponId] ?? [];
        const filteredWallet = Object.fromEntries(
          Object.entries(wallet ?? {}).filter(([couponIndex]) =>
            couponIndices.includes(Number.parseInt(couponIndex, 10) as CouponIndex)
          )
        );
        return [couponId, filteredWallet];
      })
    ) as CouponState['Wallets'];

    return { Coupons: normalizedCoupons, ModifiedCoupons, Wallets: normalizedWallets };
  } catch (error) {
    // fail silently if data from local storage isn't stringified JSON
    logger.warn('Failed to parse data to coupon state', { error });
  }

  return undefined;
}
